import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { ApiError } from "@shared-core/data/api/errors";
import type {
  Catalogue,
  DictionaryWorkbookScope,
} from "@shared-core/data/catalogue-types";
import { ownershipLabel } from "@shared-core/domain/catalogue-ownership";
import { useObjectManifest } from "@shared-core/domain/object-ui/manifest";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { paths } from "@shared-core/routing/routes";
import { ArrowLeft, Download, Loader2, Upload } from "lucide-react";
import { useCommandHost } from "../app/useCommandHost";
import { Button } from "../app/components/primitives/button";
import { Card } from "../app/components/primitives/card";
import { Checkbox } from "../app/components/primitives/checkbox";
import { Label } from "../app/components/primitives/label";
import {
  RadioGroup,
  RadioGroupItem,
} from "../app/components/primitives/radio-group";

// Dictionary and catalogues (catalogue curation P4; Ben named it on 26 Oct): download the catalogues and the customer wording as one Excel
// workbook, edit it, and submit it back. The check and the save are the manifest's own command, run in the command
// shell, so the person and Claude in Excel get the same problems by sheet and cell and the same confirmation.
// PROVISIONAL wording (docs/ui-wording.md), pending review.

const SUBMIT_COMMAND = "catalogue.submit_dictionary_workbook";

const WORDING_CHOICES: {
  value: DictionaryWorkbookScope["wording"];
  label: string;
}[] = [
  { value: "none", label: "None" },
  { value: "short", label: "Short list" },
  { value: "full", label: "Full list" },
];

function wordingFrom(value: string | null): DictionaryWorkbookScope["wording"] {
  return WORDING_CHOICES.find((c) => c.value === value)?.value ?? "none";
}

/** The sentence a refused download carries (the route's canonical 400, as the response text), else a plain failure. */
function downloadFailureMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 400) {
    try {
      const detail = JSON.parse(error.detail)?.detail;
      const first = detail?.errors?.[0]?.message;
      if (typeof first === "string") return first;
    } catch {
      // Not the canonical shape: say it plainly below.
    }
  }
  return "The workbook could not be downloaded. Try again.";
}

export function DictionaryWorkbookPage() {
  const adapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  const index = useObjectManifest();
  const { openCommand, shell: commandShell } = useCommandHost();
  const [searchParams] = useSearchParams();
  const [catalogues, setCatalogues] = useState<Catalogue[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [chosenIds, setChosenIds] = useState<string[]>(() =>
    searchParams.getAll("catalogue_id"),
  );
  const [wording, setWording] = useState<DictionaryWorkbookScope["wording"]>(
    () => wordingFrom(searchParams.get("wording")),
  );
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setLoadFailed(false);
    adapter
      .listCataloguesForManagement()
      .then(setCatalogues)
      .catch(() => setLoadFailed(true))
      .finally(() => setLoading(false));
  }, [adapter]);

  useEffect(() => {
    load();
  }, [load]);

  const download = useCallback(
    async (scope: DictionaryWorkbookScope) => {
      setDownloading(true);
      setDownloadError(null);
      try {
        const { blob, filename } =
          await adapter.downloadDictionaryWorkbook(scope);
        capabilities.downloadBlob(filename, blob);
        toast.success(`Downloaded ${filename}`);
      } catch (error) {
        setDownloadError(downloadFailureMessage(error));
      } finally {
        setDownloading(false);
      }
    },
    [adapter, capabilities],
  );

  // The export command's link opens this page with the scope in the query: the download starts once, with the
  // person's own session.
  const startedFromLink = useRef(false);
  useEffect(() => {
    const ids = searchParams.getAll("catalogue_id");
    const named = wordingFrom(searchParams.get("wording"));
    if (startedFromLink.current || (ids.length === 0 && named === "none"))
      return;
    startedFromLink.current = true;
    void download({ catalogueIds: ids, wording: named });
  }, [searchParams, download]);

  const toggle = (id: string, isChosen: boolean) =>
    setChosenIds((ids) =>
      isChosen ? [...ids, id] : ids.filter((chosen) => chosen !== id),
    );
  const hasNothingChosen = chosenIds.length === 0 && wording === "none";
  const registryOrder = (catalogues ?? [])
    .map((c) => c.id)
    .filter((id) => chosenIds.includes(id));

  return (
    <div className="space-y-6 pb-10">
      <div className="space-y-2">
        <Link
          to={paths.settingsCatalogues()}
          className="inline-flex items-center gap-1 text-sm text-[color:var(--color-primary)]"
        >
          <ArrowLeft className="size-4" /> Back to catalogues
        </Link>
        <h1>Dictionary and catalogues</h1>
        <p className="text-sm text-muted-foreground">
          Edit the catalogues and the customer wording in Excel, then submit the
          workbook. One checks every cell before anything is saved.
        </p>
      </div>

      <Card className="space-y-5 p-5">
        <h2 className="text-base font-medium">Download a workbook</h2>
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Catalogues</legend>
          {loading ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Loading…
            </p>
          ) : loadFailed ? (
            <div className="space-y-2">
              <p className="text-sm font-medium">
                The catalogues could not be loaded.
              </p>
              <Button variant="outline" size="sm" onClick={load}>
                Retry
              </Button>
            </div>
          ) : !catalogues || catalogues.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              There are no catalogues to download yet.
            </p>
          ) : (
            <ul className="space-y-2">
              {catalogues.map((c) => (
                <li key={c.id} className="flex items-center gap-2">
                  <Checkbox
                    id={`workbook-catalogue-${c.id}`}
                    checked={chosenIds.includes(c.id)}
                    onCheckedChange={(next) => toggle(c.id, next === true)}
                  />
                  <Label htmlFor={`workbook-catalogue-${c.id}`}>
                    {c.name}
                    <span className="ml-2 font-normal text-muted-foreground">
                      {ownershipLabel(c.ownedBy)}
                    </span>
                  </Label>
                </li>
              ))}
            </ul>
          )}
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Customer wording</legend>
          <RadioGroup
            value={wording}
            onValueChange={(next) => setWording(wordingFrom(next))}
            className="flex flex-wrap gap-4"
          >
            {WORDING_CHOICES.map((choice) => (
              <div key={choice.value} className="flex items-center gap-2">
                <RadioGroupItem
                  value={choice.value}
                  id={`workbook-wording-${choice.value}`}
                />
                <Label htmlFor={`workbook-wording-${choice.value}`}>
                  {choice.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </fieldset>

        <div className="space-y-2">
          <Button
            disabled={downloading || hasNothingChosen}
            onClick={() =>
              void download({ catalogueIds: registryOrder, wording })
            }
          >
            {downloading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Download className="size-4" />
            )}{" "}
            Download workbook
          </Button>
          {hasNothingChosen && (
            <p className="text-sm text-muted-foreground">
              Choose at least one catalogue, or a customer wording list.
            </p>
          )}
          {downloadError && (
            <p role="alert" className="text-sm text-red-600">
              {downloadError}
            </p>
          )}
        </div>
      </Card>

      <Card className="space-y-3 p-5">
        <h2 className="text-base font-medium">Submit an edited workbook</h2>
        <p className="text-sm text-muted-foreground">
          One lists every problem by sheet and cell. A catalogue managed in One
          is then saved as a new version; a catalogue managed in code is only
          checked.
        </p>
        <Button
          variant="outline"
          onClick={() => {
            const command = index.commandOf(SUBMIT_COMMAND);
            openCommand({
              endpoint: {
                command,
                href: command.path,
                dryRunHref: command.dry_run_path,
              },
              context: "Dictionary and catalogues",
            });
          }}
        >
          <Upload className="size-4" /> {index.commandOf(SUBMIT_COMMAND).label}
        </Button>
      </Card>
      {commandShell}
    </div>
  );
}
